import { useEffect, useMemo, useState } from "react";

import { Alert, Dialog } from "@fronterahealth/frontera-ui-components";

import {
  ReportTypeEnum,
  type TemplateListItem,
  useGetTemplateDetail,
  useListTemplates,
} from "@api/rest/types-and-hooks";
import { ProgressSummaryMissingText } from "@pages/Templates/ProgressSummaryMissingText";
import { PublishMode, type PublishModeValue, TPFieldType, TemplateStatus } from "@pages/Templates/constants";
import { missingProgressSummaryTypes } from "@pages/Templates/progressSummaryGuard";

interface PublishDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  templateName: string;
  reportType: string | null;
  currentTemplateId: number;
  isPending: boolean;
  onConfirm: (previousTemplateId: number | null) => void;
  onCancel: () => void;
}

export const PublishDialog: React.FC<PublishDialogProps> = ({
  open,
  setOpen,
  templateName,
  reportType,
  currentTemplateId,
  isPending,
  onConfirm,
  onCancel,
}) => {
  const [mode, setMode] = useState<PublishModeValue | null>(null);
  const [selectedPreviousId, setSelectedPreviousId] = useState<number | null>(null);
  // The missing list the user acknowledged; a different list needs a fresh
  // acknowledgment.
  const [acknowledgedMissing, setAcknowledgedMissing] = useState<string | null>(null);

  // Reassessments only show a saved progress summary when the template lists
  // the field. Programming-tab saves already warn, but a template can reach
  // publish without one (e.g. an Initial Assessment copy switched to
  // Reassessment), so check again here. Publish waits for fresh data and is
  // blocked if the check can't run.
  const checksProgressSummary = open && reportType === ReportTypeEnum.REASSESSMENT;
  const {
    data: detailData,
    isFetching: isDetailFetching,
    isError: isDetailError,
  } = useGetTemplateDetail(currentTemplateId, {
    query: { enabled: checksProgressSummary },
  });
  const isProgressSummaryCheckPending = checksProgressSummary && isDetailFetching;
  const progressSummaryCheckFailed =
    checksProgressSummary &&
    !isDetailFetching &&
    (isDetailError || (detailData !== undefined && detailData.status !== 200));
  const missingProgressSummary = useMemo(() => {
    const tpt = detailData?.status === 200 ? detailData.data.treatment_plan_template : null;
    if (!checksProgressSummary || !tpt) return [];
    return missingProgressSummaryTypes({
      reportType: reportType ?? "",
      isGasEnabled: tpt.is_gas_enabled,
      isAddTargetsEnabled: tpt.is_add_targets_enabled,
      fields: [...tpt.default_fields, ...tpt.domains.flatMap((d) => d.template_fields)].map((f) => ({
        type: f.type === TPFieldType.TARGET ? TPFieldType.TARGET : TPFieldType.GOAL,
        field_identifier: f.field_identifier,
      })),
    });
  }, [detailData, checksProgressSummary, reportType]);
  const missingProgressSummaryKey = missingProgressSummary.join(",");
  const progressSummaryAcknowledged = acknowledgedMissing === missingProgressSummaryKey;

  const { data: publishedData, isLoading } = useListTemplates(
    { status: TemplateStatus.PUBLISHED, report_type: reportType ?? undefined, page_size: 100 },
    { query: { enabled: open } },
  );

  const publishedTemplates: TemplateListItem[] = useMemo(
    () => (publishedData?.status === 200 ? publishedData.data.results.filter((t) => t.id !== currentTemplateId) : []),
    [publishedData, currentTemplateId],
  );

  // When in REPLACES mode, fall back to the first published template if none explicitly chosen.
  const effectiveSelectedId =
    mode === PublishMode.REPLACES ? (selectedPreviousId ?? publishedTemplates[0]?.id ?? null) : null;
  const selectedTemplate = publishedTemplates.find((t) => t.id === effectiveSelectedId) ?? null;
  const canConfirm =
    (mode === PublishMode.NEW || (mode === PublishMode.REPLACES && effectiveSelectedId !== null)) &&
    !isProgressSummaryCheckPending &&
    !progressSummaryCheckFailed &&
    (missingProgressSummary.length === 0 || progressSummaryAcknowledged);

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMode(null);
      setSelectedPreviousId(null);
      setAcknowledgedMissing(null);
    }
  }, [open]);

  return (
    <Dialog
      title={`Publish "${templateName}"`}
      open={open}
      setOpen={setOpen}
      primaryButton={{
        text: "Publish",
        onClick: () => onConfirm(mode === PublishMode.REPLACES ? effectiveSelectedId : null),
        disabled: !canConfirm || isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: onCancel }}
      size="2xl"
    >
      <p className="text-sm text-text-secondary mb-4">
        Is this a new template, or does it replace an existing published template?
      </p>

      <div className="flex flex-col gap-3 mb-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="publish-mode"
            value={PublishMode.NEW}
            checked={mode === PublishMode.NEW}
            onChange={() => {
              setMode(PublishMode.NEW);
              setSelectedPreviousId(null);
            }}
            className="mt-0.5 shrink-0"
          />
          <div>
            <p className="text-sm font-medium text-text-primary">New template</p>
            <p className="text-xs text-text-secondary">No previous version to archive.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="publish-mode"
            value={PublishMode.REPLACES}
            checked={mode === PublishMode.REPLACES}
            onChange={() => setMode(PublishMode.REPLACES)}
            className="mt-0.5 shrink-0"
          />
          <div>
            <p className="text-sm font-medium text-text-primary">Replaces an existing published template</p>
            <p className="text-xs text-text-secondary">
              The previous version will be archived and its organizations carried over to this template.
            </p>
          </div>
        </label>
      </div>

      {mode === PublishMode.REPLACES && (
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-secondary">Template to archive</label>
            {isLoading ? (
              <p className="text-sm text-text-tertiary">Loading published templates…</p>
            ) : publishedTemplates.length === 0 ? (
              <p className="text-sm text-text-tertiary">No published templates of this type found.</p>
            ) : (
              <select
                value={effectiveSelectedId ?? ""}
                onChange={(e) => setSelectedPreviousId(Number(e.target.value))}
                className="w-full rounded-md border border-border-medium bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-limestone-400"
              >
                {publishedTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (ID: {t.id})
                  </option>
                ))}
              </select>
            )}
          </div>

          {selectedTemplate && selectedTemplate.organizations.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-text-secondary">Organizations that will carry over</p>
              <div className="flex flex-wrap gap-2">
                {selectedTemplate.organizations.map((org) => (
                  <span
                    key={org.id}
                    className="inline-flex items-center rounded-full border border-border-subtle bg-bg-default px-2.5 py-0.5 text-xs text-text-secondary"
                  >
                    {org.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {selectedTemplate && selectedTemplate.organizations.length === 0 && (
            <p className="text-xs text-text-tertiary">The selected template has no organizations assigned.</p>
          )}
        </div>
      )}

      {progressSummaryCheckFailed && (
        <div className="mb-4">
          <Alert
            title="Couldn't check for the Progress Summary field"
            appearance="error"
            text={
              <p className="-mt-2">
                This reassessment template can&apos;t be published until it&apos;s checked for the Progress Summary
                field. Close this dialog and try again.
              </p>
            }
          />
        </div>
      )}

      {missingProgressSummary.length > 0 && (
        <div className="mb-4 flex flex-col gap-2">
          <Alert
            title="Progress Summary field missing"
            appearance="warn"
            text={
              <p className="-mt-2">
                <ProgressSummaryMissingText missingTypes={missingProgressSummary} />
              </p>
            }
          />
          <label className="flex items-start gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={progressSummaryAcknowledged}
              onChange={(e) => setAcknowledgedMissing(e.target.checked ? missingProgressSummaryKey : null)}
              className="mt-0.5 shrink-0"
            />
            Publish without the Progress Summary field
          </label>
        </div>
      )}

      <Alert
        title=""
        appearance="neutral"
        text={<p className="-mt-2">Publishing makes this template read-only. To make changes later, duplicate it.</p>}
      />
    </Dialog>
  );
};
